import React, { createContext, useContext, useEffect, useCallback, useMemo } from "react";
import useApi from "../services/api";
import { AuthStateContext } from "./auth-state-context";
import { isPrerendering } from "../utils/prerender";

const AuthOperationsContext = createContext({});

function AuthOperationsProvider({ children }) {
  const { getUser } = useApi();
  const { 
    user,
    userDetails,
    setUserDetails,
    setAvatarVersion
  } = useContext(AuthStateContext);

  useEffect(() => {
    // Skip user details fetching during prerendering
    if (isPrerendering()) return;
    
    if (!user?.username) return;
    
    // Check if userDetails is stale (different user)
    if (userDetails && userDetails.username === user.username) return;

    let cancelled = false;

    (async () => {
      try {
        const data = await getUser();
        if (cancelled) return;
        setUserDetails(data);
        if (typeof window !== 'undefined') {
          sessionStorage.setItem('userDetails', JSON.stringify(data));
        }
      } catch (err) {
        console.error('Error fetching user details', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.username, userDetails, getUser, setUserDetails]);

  const reloadUserDetails = useCallback(async () => {
    try {
      const data = await getUser();
      setUserDetails(data);
      setAvatarVersion(Date.now());
      sessionStorage.setItem('userDetails', JSON.stringify(data));
    } catch (err) {
      console.error('Failed to reload user details', err);
    }
  }, [getUser, setUserDetails, setAvatarVersion]);

  const value = useMemo(() => ({
    reloadUserDetails
  }), [reloadUserDetails]);

  return (
    <AuthOperationsContext.Provider value={value}>
      {children}
    </AuthOperationsContext.Provider>
  );
}

export { AuthOperationsProvider, AuthOperationsContext };